import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Tag } from '@digdir/designsystemet-react';
import type { TeamUsageDto } from '../../../../../../trustpulse-api/src/application/dto/organization/organization-usage-stats.dto';
import { Routes } from '../../../../enums/Routes';
import { buildRoute } from '../../../../utils/routeBuilder';
import { DateFormatter } from '../utils/DateFormatter';
import { PercentageCalculator } from '../utils/PercentageCalculator';
import classes from '../OrganizationUsagePage.module.css';

export interface TeamUsageTableProps {
  teams: TeamUsageDto[];
}

/**
 * Displays a table of team usage statistics
 */
export const TeamUsageTable: React.FC<TeamUsageTableProps> = ({ teams }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Perioden følger med til detaljsiden for teamet
  const periodQuery = (() => {
    const params = new URLSearchParams();
    const periodStart = searchParams.get('periodStart');
    const periodEnd = searchParams.get('periodEnd');
    if (periodStart) params.append('periodStart', periodStart);
    if (periodEnd) params.append('periodEnd', periodEnd);
    const query = params.toString();
    return query ? `?${query}` : '';
  })();

  return (
    <Table className={classes.table}>
      <Table.Head>
        <Table.Row>
          <Table.HeaderCell>
            {t('usage.overview.columns.team')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('usage.overview.columns.channels')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('usage.overview.columns.responseRate')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('usage.overview.columns.next')}
          </Table.HeaderCell>
          <Table.HeaderCell>
            {t('usage.overview.columns.last')}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {teams.map((team) => (
          <Table.Row key={team.teamId}>
            <Table.HeaderCell scope="row">
              <span className={classes.teamCell}>
                <Link
                  to={`${buildRoute(Routes.OrganizationUsageTeamDetails, { teamId: team.teamId })}${periodQuery}`}
                  className="ds-link"
                >
                  {team.teamName}
                </Link>
                <span className={classes.cellNote}>
                  {t('usage.overview.table.teamMembers', {
                    count: team.teamSize,
                  })}
                </span>
              </span>
            </Table.HeaderCell>
            <Table.Cell>
              {team.channels.length > 0 ? (
                <span className={classes.channels}>
                  {team.channels.map((channel) => (
                    <Tag key={channel} data-size="sm" data-color="neutral">
                      {channel}
                    </Tag>
                  ))}
                </span>
              ) : (
                <span className={classes.cellNote}>
                  {t('usage.overview.table.noChannels')}
                </span>
              )}
            </Table.Cell>
            <Table.Cell>
              <span className={classes.rateCell}>
                <span className={classes.cellValue}>
                  {PercentageCalculator.format(team.averageResponseRate, 1)}
                </span>
                <span aria-hidden="true" className={classes.rateTrack}>
                  <span
                    style={{
                      width: `${PercentageCalculator.cap(team.averageResponseRate)}%`,
                    }}
                  />
                </span>
                <span className={classes.cellNote}>
                  {t('usage.overview.table.responsesSent', {
                    responses: team.totalResponses,
                    sent: team.totalSurveysSent,
                  })}
                </span>
              </span>
            </Table.Cell>
            <Table.Cell>
              <span className={classes.stackCell}>
                <span className={classes.cellDate}>
                  {DateFormatter.formatShortDate(team.nextScheduledDate, '–')}
                </span>
                {team.scheduledSurveysCount > 0 && (
                  <span className={classes.cellNote}>
                    {t('usage.overview.table.plannedCount', {
                      count: team.scheduledSurveysCount,
                    })}
                    {team.recurringScheduledSurveysCount > 0 &&
                      ` · ${team.recurringScheduledSurveysCount} ${t('usage.overview.table.recurring')}`}
                    {team.recurringFrequencies?.length > 0 &&
                      ` (${team.recurringFrequencies.join(', ')})`}
                  </span>
                )}
              </span>
            </Table.Cell>
            <Table.Cell>
              <span className={classes.cellDate}>
                {DateFormatter.formatShortDate(
                  team.lastSurveyDate,
                  t('usage.overview.table.noSurveys'),
                )}
              </span>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};
